"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Vehicle, OneTimePackage } from "@/types";
import { VehicleMultiSelect } from "@/components/appointment/VehicleMultiSelect";
import { repeatedAppointmentService } from "@/services/repeatedAppointmentService";
import {
  RepeatedAppointmentWithDetails,
  WEEKDAYS,
} from "@/types/repeatedAppointment.types";
import { Subscription } from "@/types";
import { generateTimeSlots, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Package,
  ChevronDown,
  Check,
  Search,
  Calendar,
  Clock,
  CreditCard,
} from "lucide-react";
import { cn } from "@/lib/utils";

const timeSlots = generateTimeSlots();

// Helper to normalize time format for Select matching
// Backend may store "9:15AM" but Select uses "9:15 AM" as value
const normalizeTimeValue = (time: string | null): string => {
  if (!time) return "";
  // Remove all spaces and convert to uppercase for comparison
  const normalized = time.replace(/\s+/g, "").trim().toUpperCase();
  // Find matching slot by comparing normalized formats
  const slot = timeSlots.find(
    (s) => s.label.replace(/\s+/g, "").trim().toUpperCase() === normalized,
  );
  return slot ? slot.label : "";
};

interface RepeatedAppointmentSelectorProps {
  customerId: string;
  packages: OneTimePackage[];
  customerVehicles: Vehicle[];
  selectedSetupId: string | null;
  onSelect: (setup: RepeatedAppointmentWithDetails | null) => void;
  disabled?: boolean;
  // COMMENTED OUT: Subscription props disabled
  /** Filter by setup type. Accepted for the call sites but currently only 'package' setups are supported. */
  setupType?: "package" | "subscription";
  /** Available subscriptions for the customer (required if setupType is 'subscription') */
  // subscriptions?: Subscription[];
}

export function RepeatedAppointmentSelector({
  customerId,
  packages,
  customerVehicles,
  selectedSetupId,
  onSelect,
  disabled = false,
  // COMMENTED OUT: Subscription props disabled - force package type only
  // setupType = "package",
  // subscriptions = [],
}: RepeatedAppointmentSelectorProps) {
  // Force package type only (subscription disabled)
  const setupType = "package";
  // State
  const [existingSetups, setExistingSetups] = useState<
    RepeatedAppointmentWithDetails[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Create new form state
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSetup, setNewSetup] = useState({
    packageId: "",
    // subscriptionId: "", // COMMENTED OUT: Subscription disabled
    vehicleIds: [] as string[],
    preferredDay: "",
    preferredTime: "",
  });
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");

  // Fetch existing setups
  const fetchSetups = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const setups =
        await repeatedAppointmentService.getByCustomerId(customerId);
      setExistingSetups(setups);
    } catch (error) {
      console.error("Error fetching repeated appointments:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchSetups();
  }, [fetchSetups]);

  // Reset when customer changes
  useEffect(() => {
    onSelect(null);
    setIsCreating(false);
    setNewSetup({
      packageId: "",
      // subscriptionId: "", // COMMENTED OUT: Subscription disabled
      vehicleIds: [],
      preferredDay: "",
      preferredTime: "",
    });
  }, [customerId]);

  // Get selected setup details
  const selectedSetup = useMemo(() => {
    return existingSetups.find((s) => s.id === selectedSetupId) || null;
  }, [existingSetups, selectedSetupId]);

  // Filter setups based on setup type and search
  const filteredSetups = useMemo(() => {
    // First filter by setup type
    let typeFiltered = existingSetups;
    if (setupType === "package") {
      // Package setups have packageId and no userSubscriptionId
      typeFiltered = existingSetups.filter(
        (setup) => setup.packageId && !setup.userSubscriptionId,
      );
    }
    // COMMENTED OUT: Subscription filtering disabled
    // } else if (setupType === "subscription") {
    //   // Subscription setups have userSubscriptionId and no packageId
    //   typeFiltered = existingSetups.filter(
    //     (setup) => setup.userSubscriptionId && !setup.packageId,
    //   );
    // }

    // Then filter by search term
    if (!searchTerm.trim()) return typeFiltered;
    const query = searchTerm.toLowerCase();
    return typeFiltered.filter(
      (setup) =>
        setup.package?.name?.toLowerCase().includes(query) ||
        setup.userSubscription?.subscription?.name
          ?.toLowerCase()
          .includes(query) ||
        setup.preferredDay?.toLowerCase().includes(query) ||
        setup.vehicles?.some((v) =>
          v.plateNumber?.toLowerCase().includes(query),
        ),
    );
  }, [existingSetups, searchTerm, setupType]);

  const activePackages = packages
    .filter((p) => p.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedPackage = activePackages.find(
    (p) => p.id === newSetup.packageId,
  );
  // COMMENTED OUT: Subscription selection disabled
  // const selectedSubscription = subscriptions.find(
  //   (s) => s.id === newSetup.subscriptionId,
  // );

  const resetCreateForm = () => {
    setNewSetup({
      packageId: "",
      // subscriptionId: "", // COMMENTED OUT: Subscription disabled
      vehicleIds: [],
      preferredDay: "",
      preferredTime: "",
    });
    setVehicleSearchTerm("");
    setIsCreating(false);
  };

  const handleCreateNew = async () => {
    if (setupType === "package" && !newSetup.packageId) {
      toast.error("Please select a package");
      return;
    }
    // COMMENTED OUT: Subscription validation disabled
    // if (setupType === "subscription" && !newSetup.subscriptionId) {
    //   toast.error("Please select a subscription");
    //   return;
    // }
    if (newSetup.vehicleIds.length === 0) {
      toast.error("Please select at least one vehicle");
      return;
    }
    if (!newSetup.preferredDay) {
      toast.error("Please select a preferred day");
      return;
    }
    if (!newSetup.preferredTime) {
      toast.error("Please select a preferred time");
      return;
    }

    setSaving(true);
    try {
      const created = await repeatedAppointmentService.create({
        customerId,
        packageId: setupType === "package" ? newSetup.packageId : null,
        // COMMENTED OUT: Subscription disabled - always null
        userSubscriptionId: null,
        // userSubscriptionId:
        //   setupType === "subscription" ? newSetup.subscriptionId : null,
        vehicleIds: newSetup.vehicleIds,
        preferredDay: newSetup.preferredDay,
        preferredTime: newSetup.preferredTime,
      });

      toast.success("Recurring setup created successfully");
      resetCreateForm();
      await fetchSetups();
      // Auto-select the newly created setup
      onSelect(created);
      setIsOpen(false);
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to create recurring setup";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = (setup: RepeatedAppointmentWithDetails) => {
    onSelect(setup);
    setIsOpen(false);
    setIsCreating(false);
  };

  const handleClear = () => {
    onSelect(null);
  };

  if (!customerId) {
    return (
      <div className="p-3 text-sm text-muted-foreground bg-muted rounded-lg">
        Please select a customer first to view existing recurring setups.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label>Existing Recurring Setup</Label>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="w-full justify-between h-auto min-h-[40px] py-2"
            disabled={disabled || loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading setups...
              </span>
            ) : selectedSetup ? (
              <div className="flex items-center gap-2 flex-wrap">
                {selectedSetup.userSubscriptionId ? (
                  <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className="font-medium">
                  {selectedSetup.userSubscriptionId
                    ? selectedSetup.userSubscription?.subscription?.name ||
                      "Subscription"
                    : selectedSetup.package?.name}
                </span>
                <Badge
                  variant={
                    selectedSetup.userSubscriptionId ? "default" : "secondary"
                  }
                  className="shrink-0"
                >
                  {selectedSetup.userSubscriptionId
                    ? "Subscription"
                    : formatCurrency(selectedSetup.package?.price || 0)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {selectedSetup.preferredDay &&
                    `Every ${selectedSetup.preferredDay.charAt(0).toUpperCase()}${selectedSetup.preferredDay.slice(1)}`}
                  {selectedSetup.preferredTime &&
                    ` at ${selectedSetup.preferredTime}`}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                Select existing setup or create new...
              </span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          {isCreating ? (
            // Create New Form
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Create New Recurring Setup</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetCreateForm}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>

              {/* Package Selection */}
              {/* Pacakge Selection (Only if setupType is 'package') */}
              {setupType === "package" && (
                <div className="grid gap-2">
                  <Label className="text-sm">Package *</Label>
                  <Select
                    value={newSetup.packageId}
                    onValueChange={(value) =>
                      setNewSetup({ ...newSetup, packageId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select package" />
                    </SelectTrigger>
                    <SelectContent>
                      {activePackages.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No packages available
                        </div>
                      ) : (
                        activePackages.map((pkg) => (
                          <SelectItem key={pkg.id} value={pkg.id}>
                            <div className="flex items-center gap-2">
                              <span>{pkg.name}</span>
                              <Badge variant="secondary" className="ml-auto">
                                {formatCurrency(pkg.price)}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Subscription Selection (Only if setupType is 'subscription') */}
              {/* COMMENTED OUT: Subscription selection disabled
              {setupType === "subscription" && (
                <div className="grid gap-2">
                  <Label className="text-sm">Subscription *</Label>
                  <Select
                    value={newSetup.subscriptionId}
                    onValueChange={(value) =>
                      setNewSetup({ ...newSetup, subscriptionId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      {subscriptions.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No active subscriptions found
                        </div>
                      ) : (
                        subscriptions.map((sub) => (
                          <SelectItem key={sub.id} value={sub.id}>
                            <div className="flex items-center gap-2">
                              <span>{sub.planName || "Subscription"}</span>
                              <Badge variant="default" className="ml-auto">
                                {sub.status}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              */}

              {/* Vehicle Selection */}
              <div className="grid gap-2">
                <Label className="text-sm">Vehicles *</Label>
                <VehicleMultiSelect
                  vehicles={customerVehicles}
                  selectedVehicleIds={newSetup.vehicleIds}
                  onSelectionChange={(vehicleIds) =>
                    setNewSetup({ ...newSetup, vehicleIds })
                  }
                  searchTerm={vehicleSearchTerm}
                  onSearchChange={setVehicleSearchTerm}
                />
              </div>

              {/* Day and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label className="text-sm">Preferred Day *</Label>
                  <Select
                    value={newSetup.preferredDay}
                    onValueChange={(value) =>
                      setNewSetup({ ...newSetup, preferredDay: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((day) => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label className="text-sm">Preferred Time *</Label>
                  <Select
                    value={newSetup.preferredTime}
                    onValueChange={(value) =>
                      setNewSetup({ ...newSetup, preferredTime: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.value} value={slot.label}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Summary - MODIFIED: Subscription references removed */}
              {setupType === "package" &&
                newSetup.packageId &&
                newSetup.vehicleIds.length > 0 && (
                  <div className="p-3 bg-primary/5 rounded-lg text-sm">
                    <p className="font-medium">Summary:</p>
                    <p className="text-muted-foreground">
                      {selectedPackage?.name}{" "}
                      for {newSetup.vehicleIds.length} vehicle
                      {newSetup.vehicleIds.length !== 1 ? "s" : ""}
                      {newSetup.preferredDay &&
                        ` every ${newSetup.preferredDay.charAt(0).toUpperCase()}${newSetup.preferredDay.slice(1)}`}
                      {newSetup.preferredTime &&
                        ` at ${newSetup.preferredTime}`}
                    </p>
                    <p className="font-medium mt-1">
                      Amount per appointment:{" "}
                      {formatCurrency(
                        (selectedPackage?.price || 0) *
                          newSetup.vehicleIds.length,
                      )}
                    </p>
                    {/* COMMENTED OUT: Subscription summary disabled
                    {setupType === "subscription" && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Subscription-based setup (no per-appointment charge)
                      </p>
                    )}
                    */}
                  </div>
                )}

              {/* Create Button */}
              <Button
                onClick={handleCreateNew}
                disabled={
                  saving ||
                  (setupType === "package" && !newSetup.packageId) ||
                  // COMMENTED OUT: Subscription validation disabled
                  // (setupType === "subscription" && !newSetup.subscriptionId) ||
                  newSetup.vehicleIds.length === 0 ||
                  !newSetup.preferredDay ||
                  !newSetup.preferredTime
                }
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Recurring Setup
                  </>
                )}
              </Button>
            </div>
          ) : (
            // Selection List
            <div className="flex flex-col">
              {/* Search Input */}
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by package, day, or plate..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-9"
                  />
                </div>
              </div>

              {/* Existing Setups List */}
              <div className="max-h-[250px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">
                      Loading setups...
                    </span>
                  </div>
                ) : filteredSetups.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    {existingSetups.length === 0
                      ? "No existing recurring setups for this customer."
                      : "No setups match your search."}
                  </div>
                ) : (
                  filteredSetups.map((setup) => (
                    <div
                      key={setup.id}
                      className={cn(
                        "flex items-start gap-3 p-3 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0",
                        selectedSetupId === setup.id && "bg-accent",
                      )}
                      onClick={() => handleSelect(setup)}
                    >
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          {setup.userSubscriptionId ? (
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Package className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="font-medium">
                            {setup.userSubscriptionId
                              ? setup.userSubscription?.subscription?.name ||
                                "Subscription"
                              : setup.package?.name || "Package"}
                          </span>
                          <Badge
                            variant={
                              setup.userSubscriptionId ? "default" : "secondary"
                            }
                          >
                            {setup.userSubscriptionId
                              ? "Subscription"
                              : formatCurrency(setup.package?.price || 0)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          {setup.preferredDay && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {setup.preferredDay.charAt(0).toUpperCase() +
                                setup.preferredDay.slice(1)}
                            </span>
                          )}
                          {setup.preferredTime && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {setup.preferredTime}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {setup.vehicles?.slice(0, 3).map((v) => (
                            <Badge
                              key={v.id}
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              {v.plateNumber}
                            </Badge>
                          ))}
                          {setup.vehicles && setup.vehicles.length > 3 && (
                            <Badge
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              +{setup.vehicles.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      {selectedSetupId === setup.id && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Create New Option */}
              <div className="p-2 border-t">
                <Button
                  variant="ghost"
                  className="w-full justify-start h-auto py-2"
                  onClick={() => setIsCreating(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  <span>Create New Recurring Setup</span>
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Clear Selection Button */}
      {selectedSetup && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="text-muted-foreground"
        >
          Clear selection
        </Button>
      )}
    </div>
  );
}
